import React, { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { Button, Input, Spinner } from '@nextui-org/react'
import {
  HiOutlineUser,
  HiOutlineIdentification,
  HiOutlineHashtag,
  HiOutlinePencilAlt,
  HiOutlinePlusCircle,
  HiOutlineMinusCircle,
  HiOutlineTrash,
  HiOutlineSave
} from 'react-icons/hi'
import { pdf } from '@react-pdf/renderer'
import ManifiestoPickupPDF from './components/ManifiestoPickupPDF'
import { webApiService } from '@/services'
import { enqueueSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { IAppStore } from '@/models/store.model'
import { blobToBase64 } from '@/utils/blobToBase64.util'

interface IOwnProps {
  name: string;
  lastname: string;
  seals: { seal: string; seal2: string; seal3: string }[];
  signature_url: string;
  validation: boolean;
  signatureError: boolean;
  isSaveDisabled: boolean;
  loading: boolean;
  resValiditySeal: any;
}

const PickupFormSamsung: React.FC = () => {
  const sigCanvas = useRef<SignatureCanvas | null>(null)
  const userState = useSelector((store: IAppStore) => store.user)
  const [data, setData] = useState<IOwnProps>({
    name: '',
    lastname: '',
    seals: [{ seal: '', seal2: '', seal3: '' }],
    signature_url: '',
    validation: false,
    signatureError: false,
    isSaveDisabled: false,
    loading: false,
    resValiditySeal: []
  })

  const handleChange = (value: string, i: number, field: 'seal' | 'seal2' | 'seal3') => {
    const sanitized = value.replace(/\s+/g, '').toUpperCase()
    const newSeals = [...data.seals]
    newSeals[i][field] = sanitized
    setData({ ...data, seals: newSeals })
  }

  const handleClear = () => {
    sigCanvas.current?.clear()
    setData({ ...data, signatureError: false, signature_url: '' })
  }

  const handleSave = async () => {
    setData({ ...data, validation: true, loading: true })
    const signature = sigCanvas.current?.getTrimmedCanvas().toDataURL('image/png')
    const sealsList = data.seals.flatMap(seal => [seal.seal, seal.seal2, seal.seal3].filter((s) => s.trim() !== ''))

    const list = await webApiService.getAllBySeal({ seals: sealsList }).then(res => res.data)
    console.log('🚀 ~ list:', list)

    const pdfBlob = await pdf(<ManifiestoPickupPDF firma={String(signature)} detalle={{ name: data.name, lastName: data.lastname }} listado={list} />).toBlob()
    const base64PDF = await blobToBase64(pdfBlob)

    const obj = {
      seals: data.seals,
      id_user: userState.id,
      picker_firstname: data.name,
      picker_lastname: data.lastname,
      picker_url_signature: signature,
      pdf: base64PDF
    }

    try {
      const res = await webApiService.validitySeal(obj)

      if (res.success) {
        enqueueSnackbar(res.message, { variant: 'success' })
        sigCanvas.current?.clear()
        setData({
          ...data,
          name: '',
          lastname: '',
          seals: [{ seal: '', seal2: '', seal3: '' }],
          signature_url: '',
          validation: false,
          signatureError: false,
          isSaveDisabled: false,
          loading: false,
          resValiditySeal: []
        })
      } else {
        enqueueSnackbar(res.message, { variant: 'error' })
        setData({ ...data, resValiditySeal: res.data, loading: false })
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' })
      setData({ ...data, loading: false })
    }
  }

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signature = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png')
      setData((prevData) => ({ ...prevData, signature_url: signature }))
    }
  }

  const addSeal = () => {
    setData({ ...data, seals: [...data.seals, { seal: '', seal2: '', seal3: '' }] })
  }

  const handleRemoveSeal = (index: number) => {
    const newSeals = data.seals.filter((_, i) => i !== index)
    setData({ ...data, seals: newSeals })
  }

  const isSaveDisabled = !data.name ||
    !data.lastname ||
    !data.seals.every(seal => seal.seal.trim() !== '' && seal.seal2.trim() !== '') ||
    !data.signature_url

  return (
    <div className='flex flex-col gap-6 max-w-xl mx-auto'>
      {/* Card: Datos del responsable */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
        <div className='flex items-center gap-2 text-[#8492a6] text-sm font-semibold mb-4'>
          <HiOutlineUser size={18} />
          Datos del responsable
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <Input
            label='Nombre'
            variant='bordered'
            value={data.name}
            onValueChange={(value) => setData({ ...data, name: value })}
            isInvalid={data.validation && data.name.trim() === ''}
            errorMessage={data.validation && data.name.trim() === '' ? 'Debe ingresar su nombre' : ''}
            onBlur={() => setData({ ...data, validation: true })}
            startContent={<HiOutlineUser className='text-[#8492a6]' size={16} />}
            isDisabled={data.loading}
          />
          <Input
            label='Apellido'
            variant='bordered'
            value={data.lastname}
            onValueChange={(value) => setData({ ...data, lastname: value })}
            isInvalid={data.validation && data.lastname.trim() === ''}
            errorMessage={data.validation && data.lastname.trim() === '' ? 'Debe ingresar su apellido' : ''}
            onBlur={() => setData({ ...data, validation: true })}
            startContent={<HiOutlineIdentification className='text-[#8492a6]' size={16} />}
            isDisabled={data.loading}
          />
        </div>
      </div>

      {/* Card: Sellos */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2 text-[#8492a6] text-sm font-semibold'>
            <HiOutlineHashtag size={18} />
            Sellos
          </div>
          <span className='text-xs text-[#8492a6]'>{data.seals.length} {data.seals.length === 1 ? 'par' : 'pares'}</span>
        </div>

        <div className='flex flex-col gap-3'>
          {data.seals.map((seal, i) => {
            const isLast = i === data.seals.length - 1
            const bothEmpty = seal.seal === '' && seal.seal2 === ''
            return (
              <div key={i} className='flex items-center gap-3'>
                <Input
                  label={`Sello 1 (par ${i + 1})`}
                  variant='bordered'
                  value={seal.seal}
                  onValueChange={(value) => handleChange(value, i, 'seal')}
                  isInvalid={data.resValiditySeal.includes(seal.seal)}
                  isDisabled={data.loading}
                />
                <Input
                  label={`Sello 2 (par ${i + 1})`}
                  variant='bordered'
                  value={seal.seal2}
                  onValueChange={(value) => handleChange(value, i, 'seal2')}
                  isInvalid={data.resValiditySeal.includes(seal.seal2)}
                  isDisabled={data.loading}
                />
                <Input
                  label={`Sello 3 (opcional, par ${i + 1})`}
                  variant='bordered'
                  value={seal.seal3}
                  onValueChange={(value) => handleChange(value, i, 'seal3')}
                  isInvalid={seal.seal3.trim() !== '' && data.resValiditySeal.includes(seal.seal3)}
                  isDisabled={data.loading}
                />
                <div className='flex items-center gap-1'>
                  {isLast
                    ? (
                      <button
                        type='button'
                        onClick={addSeal}
                        disabled={bothEmpty || data.loading}
                        className='text-[#8492a6] hover:text-[#44526B] transition-colors disabled:opacity-30 disabled:cursor-not-allowed'
                        title='Agregar par de sellos'
                      >
                        <HiOutlinePlusCircle size={26} />
                      </button>
                      )
                    : (
                      <button
                        type='button'
                        onClick={() => handleRemoveSeal(i)}
                        disabled={data.loading}
                        className='text-[#8492a6] hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed'
                        title='Quitar par'
                      >
                        <HiOutlineMinusCircle size={26} />
                      </button>
                      )}
                  {i !== 0 && isLast && (
                    <button
                      type='button'
                      onClick={() => handleRemoveSeal(i)}
                      disabled={data.loading}
                      className='text-[#8492a6] hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed'
                      title='Quitar par'
                    >
                      <HiOutlineTrash size={22} />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Card: Firma */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-2 text-[#8492a6] text-sm font-semibold'>
            <HiOutlinePencilAlt size={18} />
            Firma
          </div>
          <button
            type='button'
            onClick={handleClear}
            disabled={data.loading}
            className='flex items-center gap-1 text-xs text-[#8492a6] hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed'
            title='Borrar firma'
          >
            <HiOutlineTrash size={16} />
            Borrar
          </button>
        </div>
        <div className={`border border-dashed border-gray-200 rounded-xl bg-[#F4F7FD] flex justify-center p-3 ${data.loading ? 'pointer-events-none opacity-60' : ''}`}>
          <SignatureCanvas
            ref={sigCanvas}
            canvasProps={{ width: 450, height: 150, className: 'sigCanvas bg-white rounded-lg' }}
            penColor='#1a202c'
            onEnd={handleSignatureEnd}
          />
        </div>
        {!data.signature_url && data.validation && (
          <p className='text-xs text-red-500 mt-2'>Debe firmar antes de guardar</p>
        )}
      </div>

      {/* Card: Acciones */}
      <div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex justify-end gap-3'>
        <Button
          variant='bordered'
          onPress={handleClear}
          isDisabled={data.loading}
          className='border-gray-200 text-[#44526B]'
        >
          Borrar Firma
        </Button>
        <Button
          onPress={handleSave}
          isDisabled={isSaveDisabled || data.loading}
          className='bg-[#44526B] text-white font-semibold'
          startContent={data.loading ? <Spinner size='sm' color='white' /> : <HiOutlineSave size={18} />}
        >
          {data.loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </div>
  )
}

export default PickupFormSamsung
